import React, { useState } from "react";
import styled from "styled-components";
import TickIcon from "../../assets/icons/TickIcon";
import ClockIcon from "../../assets/icons/ClockIcon";
import QuestionMarkIcon from "../../assets/icons/QuestionMarkIcon";
import RefreshIcon from "../../assets/icons/RefreshIcon";
import { authFetch } from "../../utils/authFetch";

interface ApprovalPendingCardProps {
    organizationName: string;
  organizationId?: string;
  membershipStatusId?: 0 | 1 | 14 | 15 | 16 | "none";
  joinRequest?: {
    id?: number;
    requestedAt?: string;
    reviewedAt?: string;
    reviewNotes?: string | null;
  };
  inModal?: boolean;
    onBackToWebsite: () => void;
    onSwitchOrganization: () => void;
    onRefreshStatus?: () => Promise<void> | void;
}

const ApprovalPendingCard: React.FC<ApprovalPendingCardProps> = ({
    organizationName,
  organizationId,
  membershipStatusId,
  joinRequest,
  inModal = false,
    onBackToWebsite,
    onSwitchOrganization,
    onRefreshStatus,
}) => {
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const status =
    membershipStatusId === 1 || membershipStatusId === 14 || membershipStatusId === 15 || membershipStatusId === 16
      ? membershipStatusId
      : joinRequest
        ? 1
        : null;

  const submittedTime = joinRequest?.requestedAt
    ? new Date(joinRequest.requestedAt).toLocaleString()
    : "Not available";
  const reviewedTime = joinRequest?.reviewedAt
    ? new Date(joinRequest.reviewedAt).toLocaleString()
    : "Not reviewed yet";

  const statusText =
    status === 14
      ? "Accepted"
      : status === 15
        ? "Rejected"
      : status === 16
        ? "Joined"
        : status === 1
        ? "Pending"
        : "Not Requested";

  const titleText = status === 16 ? "Request Approved" : status === 14 ? "Request Accepted" : status === 15 ? "Request Rejected" : "Approval Pending";

  const descriptionText =
    status === 16
      ? `You are now a member of ${organizationName}. Your join request has been approved.`
      : status === 14
      ? `Your request to join ${organizationName} has been accepted.`
      : status === 15
        ? "Your request rejected by l3 admin. you can join other organization"
        : `Your request to join ${organizationName} is currently in pending state. Once your L3 admin approves your request, you will be redirected to organization details page.`;

  const finalStepTitle = status === 16 ? "Completed" : status === 14 ? "Approved" : status === 15 ? "Rejected" : "Approve/Reject";

  const handleCancelAndSwitch = async () => {
    if (!organizationId) {
      onSwitchOrganization();
      return;
    }

    const apiBase = process.env.REACT_APP_API_URL;
    if (!apiBase) {
      setCancelError("API URL is not configured.");
      return;
    }

    setCancelError(null);
    setIsCancelling(true);

    try {
      const response = await authFetch(
        `${apiBase}/portal/organizations/${encodeURIComponent(organizationId)}/join-request`,
        { method: "DELETE" }
      );

      let responseBody: any = null;
      try {
        responseBody = await response.json();
      } catch {
        responseBody = null;
      }

      if (!response.ok) {
        throw new Error(responseBody?.message || "Failed to cancel join request.");
      }

      onSwitchOrganization();
    } catch (error) {
      setCancelError(
        error instanceof Error ? error.message : "Failed to cancel join request."
      );
    } finally {
      setIsCancelling(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);

    try {
      await onRefreshStatus?.();
    } finally {
      setIsRefreshing(false);
    }
  };

    return (
      <CardContainer $inModal={inModal}>
      <CardTitle>{titleText}</CardTitle>
            <CardDescription>
        {descriptionText.includes(organizationName) ? (
          <>
            {descriptionText.split(organizationName)[0]}
            <strong>{organizationName}</strong>
            {descriptionText.split(organizationName)[1] || ""}
          </>
        ) : (
          descriptionText
        )}
            </CardDescription>

            <ApprovalTimeline>
                <StatusSection>
                    <StatusLabel>Approval Status</StatusLabel>
          <StatusRight>
            <StatusBadge $status={status}>{statusText}</StatusBadge>
            <RefreshButton
              type="button"
              onClick={() => {
                void handleRefresh();
              }}
              disabled={isRefreshing}
              aria-label="Refetch approval status"
              title="Refetch approval status"
            >
              <RefreshIcon />
            </RefreshButton>
          </StatusRight>
                </StatusSection>
                <TimelineItem completed>
                    <TimelineMarker completed>
                        <TickIcon size={18} color="#009828" />
                    </TimelineMarker>
                    <TimelineContent>
                        <TimelineTitle>Request Submitted</TimelineTitle>
                        <TimelineTime>{submittedTime}</TimelineTime>
                    </TimelineContent>
                </TimelineItem>

                <TimelineItem completed>
                    <TimelineMarker completed>
                        <ClockIcon size={18} color="#FF6D41" />
                    </TimelineMarker>
                    <TimelineContent>
                        <TimelineTitle>Admin Review</TimelineTitle>
                    <TimelineTime>{reviewedTime}</TimelineTime>
                    </TimelineContent>
                </TimelineItem>

                <TimelineItem>
                  <TimelineMarker $rejected={status === 15} $completed={status === 16 || status === 14}>
                        {status === 16 ? (
                          <TickIcon size={18} color="#009828" />
                        ) : status === 14 ? (
                          <TickIcon size={18} color="#009828" />
                        ) : (
                          <QuestionMarkIcon size={18} color={status === 15 ? "#E33629" : "#9ca3af"} />
                        )}
                    </TimelineMarker>
                    <TimelineContent>
                    <TimelineTitle disabled={status !== 14 && status !== 15 && status !== 16} $rejected={status === 15} $completed={status === 16 || status === 14}>{finalStepTitle}</TimelineTitle>
                  <TimelineDescription $rejected={status === 15}>
                    {status === 16
                    ? "Your request has been approved and you are now a member."
                    : status === 15
                    ? "Your request rejected by l3 admin. you can join other organization"
                    : "If approved, organization will display in your account."}
                  </TimelineDescription>
                    </TimelineContent>
                </TimelineItem>
            </ApprovalTimeline>

            <ActionButtons>
                <BackToWebsiteButton onClick={onBackToWebsite}>
                    Back To Website
                </BackToWebsiteButton>
            </ActionButtons>

          {cancelError && <ActionError>{cancelError}</ActionError>}
          {status !== 15 && status !== 16 && (
            <SwitchOrgLink onClick={() => { void handleCancelAndSwitch(); }} disabled={isCancelling}>
              {isCancelling ? "Cancelling request..." : "Cancel request & switch organization"}
            </SwitchOrgLink>
          )}
        </CardContainer>
    );
};

const CardContainer = styled.div<{ $inModal?: boolean }>`
  position: relative;
  z-index: 3;
  width: ${({ $inModal }) => ($inModal ? "100%" : "min(760px, calc(100% - 48px))")};
  margin: ${({ $inModal }) => ($inModal ? "0" : "40px auto")};
  border-radius: 8px;
  padding: ${({ $inModal }) => ($inModal ? "0" : "48px 40px 40px")};
  text-align: center;
`;

const CardTitle = styled.h2`
  margin: 0 0 16px;
  font-size: 24px;
  line-height: 1.2;
  color: #003366;
  font-weight: 600;
`;

const CardDescription = styled.p`
  margin: 0 auto 40px;
  max-width: 650px;
  font-size: 14px;
  line-height: 1.6;
  color: #585558;
  font-weight: 400;

  strong {
    color: #003366;
    font-weight: 600;
  }
`;

const StatusSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5ecf5;
`;

const StatusRight = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

const StatusLabel = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #212933;
`;

const StatusBadge = styled.span<{ $status: number | null }>`
  display: inline-block;
  padding: 6px 16px;
  background: ${({ $status }) => {
    if ($status === 14 || $status === 16) {
      return "#15803d";
    }
    if ($status === 15) {
      return "#c23434";
    }
    if ($status === 1) {
      return "#ff6f3f";
    }
    return "#6b7280";
  }};
  color: white;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
`;

const RefreshButton = styled.button`
  border: none;
  background: transparent;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ApprovalTimeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 32px;
  text-align: left;
  position: relative;
  padding: 12px 20px;
  border: 1px solid #e5ecf5;
    border-radius: 8px;
      background: rgba(255, 255, 255, 0.97);
`;

const TimelineItem = styled.div<{ completed?: boolean }>`
  display: flex;
  gap: 24px;
  padding: 16px 0;
  position: relative;

  &:not(:last-child) {
    &::after {
      content: '';
      position: absolute;
      left: 15px;
      top: 52px;
      width: 2px;
      height: calc(100% + 16px);
      background: #e5ecf5;
    }
  }
`;

const TimelineMarker = styled.div<{ completed?: boolean; $rejected?: boolean; $completed?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $rejected, $completed }) => {
    if ($completed) return "rgba(0, 152, 40, 0.1)";
    if ($rejected) return "#E336291A";
    return "rgba(0, 152, 40, 0.1)";
  }};
  flex-shrink: 0;
  position: relative;
  z-index: 2;

  svg {
    display: flex;
  }
`;

const TimelineContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 4px;
`;

const TimelineTitle = styled.h4<{ disabled?: boolean; $rejected?: boolean; $completed?: boolean }>`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => {
    if (props.$rejected) return "#E33629";
    if (props.$completed) return "#15803d";
    if (props.disabled) return "#9ca3af";
    return "#212933";
  }};
`;

const TimelineDescription = styled.p<{ $rejected?: boolean }>`
  margin: 0;
  font-size: 13px;
  color: ${(props) => (props.$rejected ? "#E33629" : "#6b778a")};
  line-height: 1.4;
`;

const TimelineTime = styled.p`
  margin: 0;
  font-size: 12px;
  color: #9ca3af;
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 12px;
  margin-top: 32px;
`;

const BackToWebsiteButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  background: #0556cb;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #044aa8;
    box-shadow: 0 4px 12px rgba(5, 86, 203, 0.2);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const SwitchOrgLink = styled.button`
  background: none;
  border: none;
  color: #6b778a;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #003366;
  }

  &:disabled {
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const ActionError = styled.p`
  margin: 0 0 10px;
  color: #c23434;
  font-size: 13px;
`;

export default ApprovalPendingCard;
